import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import InviteQr from '../components/InviteQr';
import {
  GiftSheet, InviteSheet, NoSeatsSheet, RemoveStudentSheet, StudentSheet, emptyInvite,
} from '../components/CoachSheets';
import TargetsSheet, { savedText } from './TargetsSheet';
import { OfflineGroup } from './OfflineSheets';
import { CoachTopBar, useCoachRefresh } from './CoachShell';
import { shareLink, copyText, vnShortDate } from '../lib/coach';
import { useApp, useAppPath } from '../lib/AppContext';

const LABEL = 'rounded-full px-2 py-0.5 text-[11px] font-bold';
const LABELS = {
  coaching: ['Đang theo', 'bg-[#e3f1e9] text-[#17804f]'],
  invite_pending: ['Chờ nhận', 'bg-[#fdf3d7] text-[#8a6100]'],
  free: ['Free', 'bg-base-200 text-muted'],
  personal_pro: ['Pro cá nhân', 'bg-[#efe9fd] text-[#5b3fc4]'],
  gift_ended: ['Hết hạn tặng', 'bg-base-200 text-muted'],
};

function StatusLabels({ row }) {
  const [text, color] = row.status === 'ending_soon'
    ? [`Còn ${row.days_left} ngày`, 'bg-[#fdebe4] text-[#8e3414]'] : LABELS[row.status] || [];
  return (
    <span className="flex flex-wrap gap-1">
      {text && <span className={`${LABEL} ${color}`}>{text}</span>}
      {row.nudge && <span className={`${LABEL} bg-[#fdebe4] text-[#8e3414]`}>Cần nhắc</span>}
      {row.renewal_requested && <span className={`${LABEL} bg-[#fdf3d7] text-[#8a6100]`}>xin gia hạn</span>}
    </span>
  );
}

// "Ghi 6/7 ngày · đạm đạt 5/7" (protein only when the student shares meals).
function rowSubtitle(row) {
  if (row.status === 'invite_pending') return `Hết hạn ${vnShortDate(row.expires_at)}`;
  if (row.status === 'gift_ended') return `Về Free${row.renewal_requested ? ' · xin gia hạn' : ''}`;
  const parts = [`Ghi ${row.days_logged_7 || 0}/7 ngày`];
  if (row.protein_days_7 != null) parts.push(`đạm đạt ${row.protein_days_7}/7`);
  return parts.join(' · ');
}

function StudentRow({ row, onOpen, onOptions, action }) {
  const sub = rowSubtitle(row);
  return (
    <li className="flex min-h-16 items-center gap-3 py-2">
      <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => onOpen(row)}>
        <Avatar name={row.name} url={row.avatar_url} size={40} />
        <span className="min-w-0">
          <span className="block truncate font-semibold">{row.name}</span>
          <StatusLabels row={row} />
          {sub && <span className="block text-xs text-muted">{sub}</span>}
        </span>
      </button>
      {action}
      {onOptions && (
        <button type="button" className="btn btn-circle btn-ghost size-10" aria-label={`Tuỳ chọn cho ${row.name}`} onClick={() => onOptions(row)}>
          <Icon name="dots" className="size-5" />
        </button>
      )}
    </li>
  );
}

function Group({ title, children, empty }) {
  return (
    <section className="flex flex-col gap-1">
      <h2 className="px-1 pt-3 text-xs font-bold uppercase tracking-wide text-muted">{title}</h2>
      {children ? <ul className="divide-y divide-line rounded-[20px] bg-base-100 px-4">{children}</ul>
        : <p className="rounded-[20px] bg-base-100 p-4 text-sm text-muted">{empty}</p>}
    </section>
  );
}

const pill = 'btn btn-sm h-9 rounded-full border-0 bg-[#e3f1e9] px-3 text-[#17804f]';

const FILTERS = [
  { id: 'all', label: 'Tất cả' },
  { id: 'gifted', label: 'Dùng chỗ' },
  { id: 'self_joined', label: 'Tự tham gia' },
  { id: 'expired', label: 'Hết hạn' },
];

function Stat({ to, dark, label, value, note, accent }) {
  return (
    <Link to={to} className={`flex min-h-[88px] flex-col gap-0.5 rounded-[18px] p-3 ${dark ? 'bg-[#15261f] text-white' : 'bg-base-100'}`}>
      <span className={`text-[10px] font-bold uppercase leading-tight tracking-wide ${dark ? 'text-[#a9b8b0]' : accent || 'text-muted'}`}>{label}</span>
      <span className="tabular text-2xl font-extrabold">{value}</span>
      {note && <span className={`truncate text-[11px] ${dark ? 'text-[#6fd3a0]' : 'text-muted'}`}>{note}</span>}
    </Link>
  );
}

// A1 "Học viên" in coach mode: the seat, self-joined and "Cần chú ý" cards, filters, then the students using
// the coach's seats (with pending invites and seats held for removed students), those who joined with the
// code, and those whose gift ended. Requests to connect live on the Yêu cầu tab. Opening a row shows the
// student's log, read-only.
export default function StudentsTab() {
  const { api, coach } = useApp();
  const refreshCoach = useCoachRefresh();
  const path = useAppPath();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  // ④ and ⑤ share the form, so "Hết chỗ" can send the coach back to what they typed.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [noSeats, setNoSeats] = useState(false);
  const [form, setForm] = useState(emptyInvite);
  const [busy, setBusy] = useState(null);
  const [sheetError, setSheetError] = useState(null);
  const [qr, setQr] = useState(null);
  const [options, setOptions] = useState(null);
  const [gift, setGift] = useState(null); // { student, mode: 'gift' | 'extend' }
  const [removing, setRemoving] = useState(null);
  const [targets, setTargets] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => api.get('/coach/students').then(setData).catch((e) => setError(e.message)), [api]);
  useEffect(() => { load(); }, [load]);
  // "+ Mời" on the other tabs comes here with ?invite=1.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!data || params.get('invite') !== '1') return;
    setParams({}, { replace: true });
    if (data.plan?.seats_free > 0) setInviteOpen(true); else setNoSeats(true);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const say = (text) => { setToast(text); setTimeout(() => setToast(null), 3500); };

  const run = async (key, fn, done) => {
    setBusy(key);
    setSheetError(null);
    try {
      await fn();
      done?.();
      await load();
      refreshCoach();
    } catch (e) {
      if (e.code === 'no_seats') {
        // The seats changed since the list loaded (e.g. an invite from another device): show them as they are.
        setGift(null);
        setNoSeats(true);
        load();
      } else setSheetError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const sendInvite = (channel) => run(channel, async () => {
    const body = { nickname: form.nickname.trim(), duration: form.duration, note: form.note, kind: channel === 'qr' ? 'qr' : 'link',
      preset_targets: form.preset ? { calorie_goal: form.calorie_goal, protein_goal: form.protein_goal } : null };
    let invite;
    try {
      invite = await api.post('/coach/invites', body);
    } catch (e) {
      if (e.code === 'no_seats') setInviteOpen(false);
      throw e;
    }
    setInviteOpen(false);
    setForm(emptyInvite());
    if (channel === 'qr') return setQr(invite);
    const result = channel === 'copy' ? await copyText(invite.url)
      : await shareLink({ title: 'Lời mời Macro Mate', text: 'Mình tặng bạn Pro Macro Mate, nhận tại đây:', url: invite.url });
    return say(result === 'copied' ? 'Đã sao chép link mời' : 'Đã tạo lời mời');
  });

  const openRow = (row) => {
    if (row.status === 'invite_pending') return undefined;
    return navigate(path(`/clients/${row.client_id}/day`), { state: { clientName: row.name } });
  };

  const optionAction = (action) => {
    const row = options;
    setOptions(null);
    if (action === 'view') openRow(row);
    if (action === 'gift' || action === 'extend') setGift({ student: row, mode: action });
    if (action === 'remove') setRemoving(row);
    if (action === 'targets') setTargets(row);
    if (action === 'schedule') navigate(path(`/coach/plan?part=schedule&add=${row.client_id}`));
    if (action === 'stop' && window.confirm(`Ngừng tặng Pro cho ${row.name}? Học viên về bản Free ngay, chỗ được trả lại.`)) {
      run('stop', () => api.post(`/coach/students/${row.client_id}/stop_gift`), () => say(`Đã ngừng tặng Pro cho ${row.name}`));
    }
  };

  // A filter shows one group; "Cần chú ý" shows only rows that need the coach, across groups.
  const show = (group) => filter === 'all' || filter === 'attention' || filter === group;
  const needsAttention = (row) => row.status === 'ending_soon' || (row.status === 'gift_ended' && row.renewal_requested);
  const visible = (rows) => (filter === 'attention' ? rows.filter(needsAttention) : rows);
  const plan = data?.plan;
  const counts = data?.counts;
  const giftedStudents = data?.gifted.filter((r) => r.client_id) || [];
  const pendingInvites = data?.gifted.filter((r) => r.invite_id) || [];

  return (
    <div className="flex flex-col gap-3">
      <CoachTopBar>
        <Link to={path('/coach/code')} className="btn btn-circle size-11 border-line bg-base-100" aria-label="Mã HLV của tôi">
          <Icon name="qr" className="size-5" />
        </Link>
        <button type="button" className="btn h-11 rounded-full border-0 bg-primary px-4 font-bold text-primary-content"
          onClick={() => { setSheetError(null); if (plan?.seats_free > 0) setInviteOpen(true); else setNoSeats(true); }}>
          <Icon name="plus" className="size-5" /> Mời
        </button>
      </CoachTopBar>
      <h1 className="text-[26px] font-bold">Học viên</h1>

      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!data && !error && <div className="flex flex-col gap-3" aria-busy="true">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-20 rounded-[20px]" />)}</div>}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat to={path('/coach/seats')} dark label="Chỗ tặng Pro" value={<>{counts.gifted_used}<span className="text-sm font-semibold text-[#a9b8b0]">/{counts.gifted_total}</span></>}
              note={plan?.active ? `Còn ${counts.gifted_free} chỗ` : 'Mua chỗ'} />
            <Stat to={path('/coach/code')} label="Tự tham gia" accent="text-[#2f5fb3]" value={counts.self_joined} note={`Mã ${coach?.code}`} />
            <button type="button" onClick={() => setFilter('attention')}
              className="flex min-h-[88px] flex-col gap-0.5 rounded-[18px] bg-base-100 p-3 text-left">
              <span className="text-[10px] font-bold uppercase leading-tight tracking-wide text-[#c2410c]">Cần chú ý</span>
              <span className={`tabular text-2xl font-extrabold ${counts.attention > 0 ? 'text-[#c2410c]' : ''}`}>{counts.attention}</span>
              <span className="text-[11px] text-muted">sắp hết hạn, xin gia hạn</span>
            </button>
          </div>

          {data.requests.length > 0 && (
            <Link to={path('/coach/requests')} className="flex min-h-12 items-center justify-between rounded-[16px] bg-[#fdf3d7] px-4 text-sm font-semibold text-[#8a6100]">
              {data.requests.length} yêu cầu kết nối đang chờ <Icon name="chevronRight" className="size-4" />
            </Link>
          )}

          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1" role="tablist" aria-label="Lọc học viên">
            {FILTERS.map((f) => {
              // "Tất cả" counts students, as the mode sheet's "N học viên" does; a pending invite holds a seat
              // (so it counts under "Dùng chỗ") but isn't a student yet.
              const people = (rows) => rows.filter((r) => !r.invite_id).length;
              const n = f.id === 'all' ? people(data.gifted) + people(data.self_joined) + people(data.expired) : data[f.id].length;
              const active = filter === f.id;
              return (
                <button key={f.id} type="button" role="tab" aria-selected={active} onClick={() => setFilter(f.id)}
                  className={`h-9 shrink-0 rounded-full px-3.5 text-[13px] font-semibold ${active ? 'bg-[#15261f] text-white' : 'border border-line bg-base-100'}`}>
                  {f.label} {n}
                </button>
              );
            })}
            {filter === 'attention' && (
              <button type="button" role="tab" aria-selected className="h-9 shrink-0 rounded-full bg-[#c2410c] px-3.5 text-[13px] font-semibold text-white"
                onClick={() => setFilter('all')}>Cần chú ý ✕</button>
            )}
          </div>

          {show('gifted') && (filter !== 'attention' || visible(data.gifted).length > 0) && <Group title="Dùng chỗ của bạn" empty="Bấm “Mời” để tặng Pro cho học viên bằng lời mời một lần.">
            {visible(data.gifted).length > 0 && visible(data.gifted).map((row) => (
              <StudentRow key={row.invite_id ? `i${row.invite_id}` : row.link_id} row={row} onOpen={openRow}
                onOptions={row.client_id ? setOptions : null}
                action={row.invite_id
                  ? <button type="button" className={pill} disabled={!!busy} onClick={() => run(`resend${row.invite_id}`, async () => {
                    const invite = await api.post(`/coach/invites/${row.invite_id}/resend`);
                    say((await shareLink({ title: 'Lời mời Macro Mate', url: invite.url })) === 'copied' ? 'Đã sao chép link mời mới' : 'Đã gửi lại lời mời');
                  })}>Gửi lại</button>
                  : (row.status === 'ending_soon' || row.renewal_requested) && <button type="button" className={pill} onClick={() => setGift({ student: row, mode: 'extend' })}>Gia hạn</button>} />
            ))}
          </Group>}

          {show('gifted') && filter !== 'attention' && data.held.length > 0 && (
            <ul className="flex flex-col gap-2">
              {data.held.map((h) => (
                <li key={h.gift_id} className="flex items-center justify-between gap-3 rounded-[20px] border border-dashed border-line bg-[repeating-linear-gradient(135deg,transparent,transparent_8px,rgba(0,0,0,0.04)_8px,rgba(0,0,0,0.04)_16px)] p-4">
                  <span className="text-sm">1 chỗ đang giữ cho <b>{h.name}</b> (đã xoá) đến {vnShortDate(h.ends_at)}</span>
                  <button type="button" className="btn btn-sm h-9 shrink-0 rounded-full" disabled={!!busy}
                    onClick={() => window.confirm(`Trả chỗ ngay? ${h.name} sẽ về bản Free.`) && run(`release${h.gift_id}`, () => api.post(`/coach/gifts/${h.gift_id}/release`), () => say('Đã trả chỗ'))}>
                    Trả ngay
                  </button>
                </li>
              ))}
            </ul>
          )}

          {show('self_joined') && (filter !== 'attention' || visible(data.self_joined).length > 0) && <Group title="Tự tham gia" empty="Chia sẻ mã HLV để học viên tự kết nối, không tốn chỗ.">
            {visible(data.self_joined).length > 0 && visible(data.self_joined).map((row) => (
              <StudentRow key={row.link_id} row={row} onOpen={openRow} onOptions={setOptions}
                action={!row.personal_pro && <button type="button" className={pill} onClick={() => setGift({ student: row, mode: 'gift' })}>Tặng Pro</button>} />
            ))}
          </Group>}

          {filter === 'attention' && [data.gifted, data.self_joined, data.expired].every((rows) => visible(rows).length === 0) && (
            <p className="rounded-[20px] bg-base-100 p-4 text-sm text-muted">Không có học viên nào cần chú ý.</p>
          )}

          {show('expired') && visible(data.expired).length > 0 && (
            <Group title="Hết hạn tặng">
              {visible(data.expired).map((row) => (
                <StudentRow key={row.link_id} row={row} onOpen={openRow} onOptions={setOptions}
                  action={<button type="button" className={pill} onClick={() => setGift({ student: row, mode: 'extend' })}>Gia hạn</button>} />
              ))}
            </Group>
          )}

          {filter === 'all' && <OfflineGroup plan={plan} onChanged={load} say={say} focus={params.get('offline') === '1'} />}
        </>
      )}

      {toast && <div role="status" className="fixed inset-x-4 bottom-28 z-40 mx-auto max-w-sm rounded-2xl bg-[#17201b] px-4 py-3 text-center text-sm font-semibold text-white">{toast}</div>}

      <InviteSheet open={inviteOpen} form={form} setForm={setForm} plan={plan} busy={busy} error={sheetError}
        onClose={() => setInviteOpen(false)} onSend={sendInvite} />
      <NoSeatsSheet open={noSeats} plan={plan} invites={pendingInvites} students={giftedStudents}
        onClose={() => setNoSeats(false)}
        onBuy={() => { setNoSeats(false); navigate(path('/coach/seats')); }}
        onRevoke={(i) => run(`revoke${i.invite_id}`, () => api.post(`/coach/invites/${i.invite_id}/revoke`), () => { setNoSeats(false); setInviteOpen(true); })}
        onEnd={(s) => { setNoSeats(false); setRemoving(s); }} />
      {qr && <InviteQr invite={qr} onClose={() => { setQr(null); load(); }} />}
      <StudentSheet student={options} onClose={() => setOptions(null)} onAction={optionAction} />
      <TargetsSheet student={targets} onClose={() => setTargets(null)} onSaved={(saved) => say(savedText(saved))} />
      <GiftSheet student={gift?.student} mode={gift?.mode} plan={plan} busy={!!busy} error={sheetError}
        onClose={() => { setGift(null); setSheetError(null); }}
        onConfirm={(duration) => run('gift', () => api.post(`/coach/students/${gift.student.client_id}/${gift.mode === 'extend' ? 'extend' : 'gift'}`, { duration }),
          () => { say(gift.mode === 'extend' ? `Đã gia hạn Pro cho ${gift.student.name}` : `Đã tặng Pro cho ${gift.student.name}`); setGift(null); })} />
      <RemoveStudentSheet student={removing} busy={!!busy} onClose={() => setRemoving(null)}
        onConfirm={(keepPro) => run('remove', () => api.del(`/coach/students/${removing.client_id}`, { keep_pro: keepPro }),
          () => { say(`Đã xoá ${removing.name} khỏi danh sách`); setRemoving(null); })} />
    </div>
  );
}
